import {Options, TableDataRequest} from '@fl/cmsch-fe-library';
import {Opt} from 'ts-opt';

import {AnalysisProtocol} from 'api/gen/AnalysisProtocol';
import {ApcStableDate} from 'api/gen/ApcStableDate';
import {CorrectionReport} from 'api/gen/CorrectionReport';
import {CorrectionReportDetail} from 'api/gen/CorrectionReportDetail';
import {CorrectionType} from 'api/gen/CorrectionType';
import {Date} from 'api/gen/Date';
import {EarTag} from 'api/gen/EarTag';
import {PagedModelCorrectionReport} from 'api/gen/PagedModelCorrectionReport';
import {StableCode} from 'api/gen/StableCode';
import {AsyncValidationSource} from 'utils/async-validation-source';

import {ApcReportFormValues} from '../components/apc-report-form/form-values';
import {AnalysisProtocolCorrectionLoadingId} from '../types/analysis-protocol-correction-loading-id';

/** Action creators for the analysis protocol correction Redux slice. */
export const analysisProtocolCorrectionAction = {
    getCorrectionReports: (tableDataRequest: Opt<TableDataRequest<CorrectionReport>>) => ({
        type: 'analysisProtocolCorrection/GET_CORRECTION_REPORTS',
        payload: {tableDataRequest},
    }) as const,
    getCorrectionReportsSuccess: (tableData: Opt<PagedModelCorrectionReport>) => ({
        type: 'analysisProtocolCorrection/GET_CORRECTION_REPORTS_SUCCESS',
        payload: tableData,
    }) as const,
    deleteCorrectionReport: (id: number, isCreatedCorrectionReport: boolean) => ({
        type: 'analysisProtocolCorrection/DELETE_CORRECTION_REPORT',
        payload: {id, isCreatedCorrectionReport},
    }) as const,
    deleteCorrectionReportSuccess: (id: number) => ({
        type: 'analysisProtocolCorrection/DELETE_CORRECTION_REPORT_SUCCESS',
        payload: {id},
    }) as const,
    exportCorrectionReports: () => ({
        type: 'analysisProtocolCorrection/EXPORT_CORRECTION_REPORTS',
    }) as const,
    setLoading: (id: AnalysisProtocolCorrectionLoadingId, loading: boolean) => ({
        type: 'analysisProtocolCorrection/SET_LOADING',
        payload: {id, loading},
    }) as const,
    getStableDateRelations: (
        focusTechnicianNumber: () => void,
        earTag: EarTag,
        correctionType: CorrectionType | undefined,
    ) => ({
        type: 'analysisProtocolCorrection/GET_STABLE_DATE_RELATIONS',
        payload: {focusTechnicianNumber, earTag, correctionType},
    }) as const,
    getStableDateRelationsSuccess: (apcStableDate: Opt<ApcStableDate>) => ({
        type: 'analysisProtocolCorrection/GET_STABLE_DATE_RELATIONS_SUCCESS',
        payload: {apcStableDate},
    }) as const,
    setProficiencyTestDateOptions: (options: Options<string>) => ({
        type: 'analysisProtocolCorrection/SET_PROFICIENCY_TEST_DATE_OPTIONS',
        payload: {options},
    }) as const,
    setStableOptions: (options: Options<string>) => ({
        type: 'analysisProtocolCorrection/SET_STABLE_OPTIONS',
        payload: {options},
    }) as const,
    getAnalysisProtocol: (earTag: EarTag, proficiencyTestDate: Date) => ({
        type: 'analysisProtocolCorrection/GET_ANALYSIS_PROTOCOL',
        payload: {earTag, proficiencyTestDate},
    }) as const,
    getAnalysisProtocolSuccess: (analysisProtocol: AnalysisProtocol) => ({
        type: 'analysisProtocolCorrection/GET_ANALYSIS_PROTOCOL_SUCCESS',
        payload: {analysisProtocol},
    }) as const,
    getStableDateRelation: (stableCode: StableCode, proficiencyTestDate: Date) => ({
        type: 'analysisProtocolCorrection/GET_STABLE_DATE_RELATION',
        payload: {stableCode, proficiencyTestDate},
    }) as const,
    createCorrectionReport: (
        formValues: ApcReportFormValues,
        focusEarTag: () => void,
        editingCorrectionReportId?: number,
    ) => ({
        type: 'analysisProtocolCorrection/CREATE_CORRECTION_REPORT',
        payload: {formValues, focusEarTag, editingCorrectionReportId},
    }) as const,
    createCorrectionReportSuccess: (correctionReport: CorrectionReportDetail) => ({
        type: 'analysisProtocolCorrection/CREATE_CORRECTION_REPORT_SUCCESS',
        payload: {correctionReport},
    }) as const,
    resetCreatedApcReports: () => ({
        type: 'analysisProtocolCorrection/RESET_CREATED_APC_REPORTS',
    }) as const,
    validateCorrectionReport: (
        focus: () => void,
        getStableDateRelations: boolean | undefined,
        source: AsyncValidationSource | undefined,
    ) => ({
        type: 'analysisProtocolCorrection/VALIDATE_CORRECTION_REPORT',
        payload: {focus, getStableDateRelations, source},
    }) as const,
    setEditingCorrectionReportId: (id?: number) => ({
        type: 'analysisProtocolCorrection/SET_EDITING_CORRECTION_REPORT_ID',
        payload: {id},
    }) as const,
    resetFormData: () => ({
        type: 'analysisProtocolCorrection/RESET_FORM_DATA',
    }) as const,
    getCorrectionReportDetail: (focus: () => void, apcId: number) => ({
        type: 'analysisProtocolCorrection/GET_CORRECTION_REPORT_DETAIL',
        payload: {focus, apcId},
    }) as const,
    clearStableAndDateData: () => ({
        type: 'analysisProtocolCorrection/clear_stable_and_date_data',
    }) as const,
};

/** Action type for fetching APC overview rows. */
export type GetCorrectionReportsAction = ReturnType<typeof analysisProtocolCorrectionAction.getCorrectionReports>;
/** Action type for clearing APC stable/date-dependent state. */
export type ClearStableAndDateDataAction = ReturnType<typeof analysisProtocolCorrectionAction.clearStableAndDateData>;
/** Action type for storing fetched APC overview rows. */
export type GetCorrectionReportsSuccessAction =
    ReturnType<typeof analysisProtocolCorrectionAction.getCorrectionReportsSuccess>;
/** Action type for deleting one correction report. */
export type DeleteCorrectionReportAction = ReturnType<typeof analysisProtocolCorrectionAction.deleteCorrectionReport>;
/** Action type for removing a deleted correction report from local state. */
export type DeleteCorrectionReportSuccessAction =
    ReturnType<typeof analysisProtocolCorrectionAction.deleteCorrectionReportSuccess>;
/** Action type for exporting correction reports. */
export type ExportCorrectionReportAction = ReturnType<typeof analysisProtocolCorrectionAction.exportCorrectionReports>;
/** Action type for toggling APC loading flags. */
export type AnalysisProtocolCorrectionSetLoadingAction = ReturnType<typeof analysisProtocolCorrectionAction.setLoading>;
/** Action type for loading APC stable/date relations. */
export type GetStableDateRelationsAction = ReturnType<typeof analysisProtocolCorrectionAction.getStableDateRelations>;
/** Action type for storing APC stable/date relations. */
export type GetStableDateRelationsSuccessAction =
    ReturnType<typeof analysisProtocolCorrectionAction.getStableDateRelationsSuccess>;
/** Action type for updating APC proficiency-test date options. */
export type SetProficiencyTestDateOptionsAction =
    ReturnType<typeof analysisProtocolCorrectionAction.setProficiencyTestDateOptions>;
/** Action type for updating APC stable options. */
export type SetStableOptionsAction = ReturnType<typeof analysisProtocolCorrectionAction.setStableOptions>;
/** Action type for loading protocol details into the APC form. */
export type GetAnalysisProtocolAction = ReturnType<typeof analysisProtocolCorrectionAction.getAnalysisProtocol>;
/** Action type for storing loaded APC protocol details. */
export type GetAnalysisProtocolSuccessAction =
    ReturnType<typeof analysisProtocolCorrectionAction.getAnalysisProtocolSuccess>;
/** Action type for loading one APC stable/date fallback relation. */
export type GetStableDateRelationAction = ReturnType<typeof analysisProtocolCorrectionAction.getStableDateRelation>;
/** Action type for creating or updating an APC report. */
export type CreateCorrectionReportAction = ReturnType<typeof analysisProtocolCorrectionAction.createCorrectionReport>;
/** Action type for storing a created or updated APC report. */
export type CreateCorrectionReportSuccessAction =
    ReturnType<typeof analysisProtocolCorrectionAction.createCorrectionReportSuccess>;
/** Action type for clearing created APC reports from local state. */
export type ResetCreatedApcReportsAction = ReturnType<typeof analysisProtocolCorrectionAction.resetCreatedApcReports>;
/** Action type for validating APC form data asynchronously. */
export type ValidateCorrectionReportAction =
    ReturnType<typeof analysisProtocolCorrectionAction.validateCorrectionReport>;
/** Action type for changing the currently edited APC report id. */
export type SetEditingCorrectionReportIdAction =
    ReturnType<typeof analysisProtocolCorrectionAction.setEditingCorrectionReportId>;
/** Action type for resetting APC form-dependent state. */
export type ResetFormDataAction = ReturnType<typeof analysisProtocolCorrectionAction.resetFormData>;
/** Action type for loading APC report detail for editing. */
export type GetCorrectionReportDetailAction =
    ReturnType<typeof analysisProtocolCorrectionAction.getCorrectionReportDetail>;

/** Union of all analysis protocol correction actions. */
export type AnalysisProtocolCorrectionAction =
    | GetCorrectionReportsAction
    | ClearStableAndDateDataAction
    | GetCorrectionReportsSuccessAction
    | DeleteCorrectionReportAction
    | DeleteCorrectionReportSuccessAction
    | ExportCorrectionReportAction
    | AnalysisProtocolCorrectionSetLoadingAction
    | GetStableDateRelationsAction
    | GetStableDateRelationsSuccessAction
    | SetProficiencyTestDateOptionsAction
    | SetStableOptionsAction
    | GetAnalysisProtocolAction
    | GetAnalysisProtocolSuccessAction
    | GetStableDateRelationAction
    | CreateCorrectionReportAction
    | CreateCorrectionReportSuccessAction
    | ResetCreatedApcReportsAction
    | ValidateCorrectionReportAction
    | SetEditingCorrectionReportIdAction
    | ResetFormDataAction
    | GetCorrectionReportDetailAction
;
